/**
 * Responsive utilities.
 *
 * Note: Avoid using these component in footer, header, and other places
 * where duplication of elements in the DOM can lead to SEO issues.
 * For those cases pair these with viewport custom hook.
 */
import React, {ReactNode, forwardRef} from 'react'
import {Box, BoxProps, ButtonProps, GridItemProps, Show} from '@chakra-ui/react'
import themeBreakpoints from '../../../theme/foundations/breakpoints'

/**
 * Key of theme breakpoints defined globally for Chakra.
 */
type Breakpoint = keyof typeof themeBreakpoints.breakpoints

/**
 * A record to setup a number value for each available breakpoint.
 */
type ThemeBreakpoints = Record<Breakpoint, number>

type WrapperProps = BoxProps & GridItemProps & ButtonProps

interface ResponsiveProps extends WrapperProps {
    children: ReactNode
    breakpoint?: Breakpoint
    startBreakpoint?: Breakpoint
    endBreakpoint?: Breakpoint
}

/**
 * Render the children in the DOM but visually hide them on desktop
 * @param children - isomorphic components used within a responsive design
 * @param rest - additional props to be passed to the Box component
 */
const HideOnDesktop = forwardRef<HTMLDivElement, ResponsiveProps>(({children, ...rest}, ref) => (
    <Box ref={ref} display={{base: 'block', md: 'none'}} {...rest}>
        {children}
    </Box>
))

/**
 * Render the children in the DOM but visually hide them on mobile
 * @param children - isomorphic components used within a responsive design
 * @param rest - additional props to be passed to the Box component
 */
const HideOnMobile = forwardRef<HTMLDivElement, ResponsiveProps>(({children, ...rest}, ref) => (
    <Box ref={ref} display={{base: 'none', md: 'block'}} {...rest}>
        {children}
    </Box>
))

/**
 * ShowBelowViewport shows its children below the specified breakpoint.
 * @param {React.ReactNode} children - The elements to conditionally render.
 * @param {Breakpoint} props.breakpoint - The breakpoint below which to show the elements.
 * @param {BoxProps} props.rest - Additional props that can be passed to the Box component.
 * @returns {JSX.Element} A Box component that shows its children below the specified breakpoint.
 */
const ShowBelowViewport = ({children, breakpoint, ...rest}: ResponsiveProps) => (
    <Show below={breakpoint} {...rest}>
        {children}
    </Show>
)

/**
 * ShowFromViewport shows its children from the specified breakpoint onwards.
 * @param {React.ReactNode} children - The elements to conditionally render.
 * @param {Breakpoint} props.breakpoint - The breakpoint from which to show the elements.
 * @param {BoxProps} props.rest - Additional props that can be passed to the Box component.
 * @returns {JSX.Element} A Box component that shows its children from the specified breakpoint onwards.
 */
const ShowFromViewport = ({children, breakpoint, ...rest}: ResponsiveProps) => (
    <Show above={breakpoint} {...rest}>
        {children}
    </Show>
)

/**
 * HideFromViewport hides its children from the specified breakpoint onwards.
 * @param {React.ReactNode} children - The elements to conditionally render.
 * @param {Breakpoint} props.breakpoint - The breakpoint from which to hide the elements.
 * @param {BoxProps} props.rest - Additional props that can be passed to the Box component.
 * @returns {JSX.Element} A Box component that hides its children from the specified breakpoint onwards.
 */
const HideFromViewport = forwardRef<HTMLDivElement, ResponsiveProps>(({children, breakpoint, ...rest}, ref) => (
    <Box ref={ref} hideFrom={breakpoint} {...rest}>
        {children}
    </Box>
))

/**
 * HideBelowViewport hides its children below the specified breakpoint.
 * @param {React.ReactNode} children - The elements to conditionally render.
 * @param {Breakpoint} props.breakpoint - The breakpoint below which to hide the elements.
 * @param {BoxProps | GridProps | ButtonProps} props.rest - Additional props that can be passed to the Box component.
 * @returns {JSX.Element} A Box component that hides its children below the specified breakpoint.
 */
const HideBelowViewport = forwardRef<HTMLDivElement, ResponsiveProps>(({children, breakpoint, ...rest}, ref) => (
    <Box ref={ref} hideBelow={breakpoint} {...rest}>
        {children}
    </Box>
))

/**
 * HideBetweenViewports hides its children between the specified breakpoints.
 * @param {React.ReactNode} children - The elements to conditionally render.
 * @param {Breakpoint} props.startBreakpoint - The breakpoint from which to start hiding the elements.
 * @param {Breakpoint} props.endBreakpoint - The breakpoint at which to stop hiding the elements.
 * @param {BoxProps} props.rest - Additional props that can be passed to the Box component.
 * @returns {JSX.Element} A Box component that hides its children between the specified breakpoints.
 */
const HideBetweenViewports = forwardRef<HTMLDivElement, ResponsiveProps>(({children, startBreakpoint, endBreakpoint, ...rest}, ref) => (
    <Box ref={ref} hideBelow={endBreakpoint} hideFrom={startBreakpoint} {...rest}>
        {children}
    </Box>
))

HideOnDesktop.displayName = 'HideOnDesktop'
HideOnMobile.displayName = 'HideOnMobile'
HideFromViewport.displayName = 'HideFromViewport'
HideBelowViewport.displayName = 'HideBelowViewport'
HideBetweenViewports.displayName = 'HideBetweenViewports'

export {
    ShowBelowViewport,
    ShowFromViewport,
    HideOnDesktop,
    HideOnMobile,
    HideFromViewport,
    HideBelowViewport,
    HideBetweenViewports,
    Breakpoint,
    ThemeBreakpoints,
}
